import { useFacilityContext } from '@aspendental/shared-components-web';
import {
	BinaryChoice,
	BinaryChoiceSchema,
	Form,
	MaskedPhoneNumberInput,
	MaskedPhoneNumberInputSchema,
	SchemaBuilder,
	TextInput,
	useHookForm,
} from '@aspendental/shared-forms-web';
import Grid from '@mui/material/Grid';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { type FieldValues } from 'react-hook-form';

import { REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { DataLayerStepNames } from '@src/constants';
import { EventAdditionalPatientInformationValueEventTypeEnum } from '@src/lib/__generated/user-events-api';
import { dataLayer } from '@src/services/dataLayer';
import { userEvent } from '@src/services/userEvent';
import {
	PatientInformationSchema,
	PatientInformationSchemaES,
	useAppStore,
	useAppointmentStore,
	useFormSectionStore,
	usePatientInformationFormStore,
	useReasonForVisitStore,
} from '@src/store';

const AdditionalPatientInformation = ({ formId }: { formId: string }) => {
	const {
		email,
		setEmail,
		phonenumber,
		setPhonenumber,
		hasInsurance,
		setHasInsurance,
		firstName,
		lastName,
		dateOfBirth,
	} = usePatientInformationFormStore((store) => store);
	const completeActiveNode = useFormSectionStore((store) => store.completeActiveNode);
	const { appointmentFacility, appointmentSlot } = useAppointmentStore((store) => store);
	const { reasonForVisit } = useReasonForVisitStore((store) => store);
	const webFacilityDetails = useFacilityContext();
	const { mottoFlow } = useAppStore((store) => store);
	const { t } = useTranslation('additionalPatientInformation');
	const router = useRouter();
	const { locale } = router;

	useEffect(() => {
		dataLayer({
			language_preference: locale,
			brand: mottoFlow ? 'mottoaligners' : 'aspendental',
			lead_score: appointmentSlot?.patientValue,
			lead_tier_national: appointmentSlot?.nationalPatientTier,
			lead_tier_office: appointmentSlot?.patientTier,
			step_name: DataLayerStepNames.additionalPatientInfo,
			reason_for_visit: reasonForVisit,
			reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
			...(webFacilityDetails?.officeInfo?.code && {
				website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
			}),
			...(webFacilityDetails?.officeInfo?.name && {
				website_selected_facility_name: webFacilityDetails?.officeInfo?.name,
			}),
			...(appointmentFacility?.facilityCode && {
				appointment_facility_id: appointmentFacility?.facilityCode,
			}),
			...(appointmentFacility?.title && {
				appointment_facility_name: appointmentFacility?.title,
			}),
		});
	}, [
		reasonForVisit,
		webFacilityDetails?.officeInfo?.code,
		webFacilityDetails?.officeInfo?.name,
		appointmentFacility?.facilityCode,
		appointmentFacility?.title,
		locale,
		mottoFlow,
		appointmentSlot,
	]);

	const schema = SchemaBuilder([
		{
			name: 'email',
			validation: locale === 'en-US' ? PatientInformationSchema.email : PatientInformationSchemaES.email,
		},
		{ name: 'phonenumber', validation: MaskedPhoneNumberInputSchema.options.default },
		{ name: 'hasInsurance', validation: BinaryChoiceSchema.options.default },
	]);

	const defaultValues = {
		email,
		phonenumber,
		hasInsurance,
	};

	const form = useHookForm({
		schema,
		defaultValues,
	});

	const handleSubmit = (fields: FieldValues) => {
		const { email, phonenumber, hasInsurance } = fields;

		setEmail(email);
		setPhonenumber(phonenumber.replace(/[^\d]/g, ''));
		setHasInsurance(hasInsurance);

		completeActiveNode();
		userEvent({
			event_type: EventAdditionalPatientInformationValueEventTypeEnum.AdditionalPatientInfo,
			event_data: {
				facility_code: appointmentFacility?.facilityCode ?? '',
				first_name: firstName,
				last_name: lastName,
				date_of_birth: new Intl.DateTimeFormat('en-US', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
				}).format(dateOfBirth ?? undefined),
				email_address: email,
				mobile_phone: phonenumber,
				is_insured: hasInsurance,
			},
		});
	};

	return (
		<Form form={form} onSubmit={handleSubmit} formId={formId}>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={7}>
					<TextInput
						name="email"
						label={t('emailLabel')}
						enforceAlphaNumeric={false}
						placeholder="email@domain.com"
					/>
				</Grid>
				<Grid item xs={12} sm={7}>
					<MaskedPhoneNumberInput name="phonenumber" label={t('phoneNumLabel')} />
				</Grid>
				<Grid item xs={12} sm={7}>
					<BinaryChoice
						groupLabel={t('hasInsurance')}
						name="hasInsurance"
						defaultSelection={defaultValues.hasInsurance}
						truthyLabel={t('yes')}
						falsyLabel={t('no')}
						variant="bodyLargeBook"
						helperMessage={t('selectionReq')}
					/>
				</Grid>
			</Grid>
		</Form>
	);
};

export default AdditionalPatientInformation;
